package terraultima

import kotlin.math.max
import kotlin.random.Random

/**
 * A deliberately tiny expression language for authored content.
 *
 * Design constraint (D001 C9): an LLM must be able to write valid conditions and effects knowing
 * only a one-page grammar and the vocabulary lists. So: no nesting, no arithmetic beyond one
 * operator, one statement per string. Anything more complex is a signal that the *simulation*
 * should be doing the work, not the content.
 *
 * CONDITIONS
 *   cap:FABRICATE            !cap:FABRICATE          capability present / absent
 *   flag:mutiny_crushed      !flag:first_grave       fact set / not set
 *   phase == SURFACE         phase != SPACE
 *   res.food < 12            gauge.morale >= 40      pressure.dissent >= 0.5
 *   axis.piety > 0.4         planet.water <= 0.2     pop.total >= 12
 *   year >= 10               mod.hab_dome >= 1       (module installed & functional)
 *   "A | B | C"              -> alternation (any of)
 *
 * EFFECTS
 *   res.food -= 5            gauge.morale += 8       pressure.entropy += 0.12
 *   pop.adults -= 2          kill:3                  (deaths spread across cohorts)
 *   flag:ai_triage = true    flag:quarantine = true for 5y    flag:ai_triage = false
 *   schedule:evt_id in 3..8y                          (memory / delayed consequence)
 *   ambition:hall                                    (start a multi-year undertaking)
 *   damage:{subsystem} 0.4   damage:water_loop 0.4    (module health loss; slot-bound allowed)
 *   install:smelter                                  (the colony BUILDS a module and keeps it)
 *   grant:VOTE               revoke:VOTE             (a capability acquired or lost by institution)
 *   thread.var:tension += 1                           (scoped local state, current thread only)
 */
object Expr {

    /**
     * The closed set of writable paths. An effect naming anything else used to SILENTLY DO NOTHING
     * (`when` with no else, `map[k]?.let{}` on an unknown key), so `pressure.trust` or `gauge.hope`
     * — exactly the plausible-but-wrong paths an LLM invents at event #700 — passed validation and
     * then had no effect for the life of the corpus. This set makes them build errors.
     */
    val WRITABLE: Set<String> =
        listOf("food", "water", "spares", "medicine", "deltaV", "cultivated", "data", "energy").map { "res.$it" }
            .plus(listOf("adults", "children", "elders", "cryo").map { "pop.$it" })
            .plus(GAUGES.map { "gauge.$it" })
            .plus(PRESSURES.map { "pressure.$it" })
            .toSet()

    /** The closed tag vocabulary. A tag outside it accumulates into a ledger nobody reads. */
    val TAGS: Set<String> = AXES.values.flatMap { listOf(it.first, it.second) }.toSet()

    // ------------------------------------------------------------------ read
    fun value(s: WorldState, path: String): Double? {
        if (path == "year") return s.year.toDouble()
        if (path == "month") return s.month.toDouble()
        val i = path.indexOf('.')
        if (i < 0) return null
        val ns = path.substring(0, i)
        val k = path.substring(i + 1)
        return when (ns) {
            "res" -> when (k) {
                "food" -> s.food; "water" -> s.water; "spares" -> s.spares
                "medicine" -> s.medicine; "deltaV" -> s.deltaV
                "cultivated" -> s.cultivated; "data" -> s.data; "energy" -> s.powerCommit
                else -> null
            }
            "pop" -> when (k) {
                "adults" -> s.adults; "children" -> s.children; "elders" -> s.elders
                "cryo" -> s.cryo; "total" -> s.popTotal
                else -> null
            }
            "gauge" -> s.gauge[k]
            "pressure" -> s.pressure[k]
            "axis" -> s.axis(k)
            "planet" -> when (k) {
                "gravity" -> s.planet.gravity; "pressure" -> s.planet.pressure
                "temp" -> s.planet.temp; "water" -> s.planet.water
                "flora" -> s.planet.flora; "fauna" -> s.planet.fauna
                "seismic" -> s.planet.seismic; "toxicity" -> s.planet.toxicity
                "insolation" -> s.planet.insolation
                "ore" -> s.planet.ore
                "dayLength" -> s.planet.dayLength
                else -> null
            }
            "thread" -> null   // thread vars are read via `Expr.threadVar`, not the global paths
            "sim" -> when (k) {
                "powerRatio" -> Physics.powerRatio(s)
                "broken" -> s.installed.count { (s.moduleHealth[it] ?: 0.0) <= 0.25 }.toDouble()
                "fed" -> s.food / kotlin.math.max(1.0, s.popTotal * 6.0)
                // Direction, not level. A colony's stores being LARGE says nothing — the launch
                // reserve is large by design — so "there is a surplus" has to mean rising.
                "foodTrend" -> Physics.trend(s, 0, s.food)
                // The fraction of the colony that fits in the storm shelter. A body may only say
                // "there is room for half of you" when this says so, and the overview screen shows
                // the same two numbers to the player.
                "shelterShare" -> if (Physics.shelterFor(s) <= 0.0) 0.0
                                  else Physics.shelterFor(s) / kotlin.math.max(1.0, s.popTotal)
                "deaths" -> s.deaths
                "deathsNotable" -> s.deathsNotable
                // Years on the ground, NOT years since launch. An event that says "it has been
                // 'the site' for four years" was gated on `year >= 4` and fired the month a
                // twenty-year voyage landed. Reads -1 while still in space, so `>=` never passes.
                "settledYears" -> if (s.landfallMonth < 0) -1.0
                                  else (s.month - s.landfallMonth) / 12.0
                else -> null
            }
            "mod" -> if (s.installed.contains(k) && (s.moduleHealth[k] ?: 0.0) > 0.25) 1.0 else 0.0
            else -> null
        }
    }

    private val CMP = Regex("""^\s*([A-Za-z_][A-Za-z0-9_.]*)\s*(<=|>=|==|!=|<|>)\s*(-?[A-Za-z0-9_.]+)\s*$""")

    fun cond(s: WorldState, caps: Set<String>, raw: String): Boolean {
        if (raw.contains('|')) return raw.split('|').any { cond(s, caps, it.trim()) }
        var c = raw.trim()
        var neg = false
        if (c.startsWith("!")) { neg = true; c = c.substring(1).trim() }
        val r = when {
            c.startsWith("cap:") -> caps.contains(c.removePrefix("cap:"))
            c.startsWith("flag:") -> s.hasFlag(c.removePrefix("flag:"))
            else -> {
                val m = CMP.find(c) ?: error("unparseable condition: '$raw'")
                val (lhs, op, rhs) = m.destructured
                if (lhs == "phase") {
                    val eq = s.phase.name == rhs
                    if (op == "==") eq else if (op == "!=") !eq else error("bad phase op in '$raw'")
                } else {
                    val l = value(s, lhs) ?: error("unknown path '$lhs' in '$raw'")
                    val rv = rhs.toDoubleOrNull() ?: error("non-numeric rhs in '$raw'")
                    when (op) {
                        "<" -> l < rv; "<=" -> l <= rv; ">" -> l > rv; ">=" -> l >= rv
                        "==" -> kotlin.math.abs(l - rv) < 1e-9; "!=" -> kotlin.math.abs(l - rv) >= 1e-9
                        else -> false
                    }
                }
            }
        }
        return if (neg) !r else r
    }

    fun all(s: WorldState, caps: Set<String>, cs: List<String>): Boolean = cs.all { cond(s, caps, it) }

    // ----------------------------------------------------------------- write
    private fun bump(s: WorldState, path: String, op: String, v: Double) {
        val i = path.indexOf('.')
        if (i < 0) return
        val ns = path.substring(0, i); val k = path.substring(i + 1)
        fun app(cur: Double): Double = when (op) { "+=" -> cur + v; "-=" -> cur - v; else -> v }
        when (ns) {
            // Stocks never go negative from an authored effect: an event that spends more than the
            // colony has spends what it has. Going negative is the SIMULATION's business (a deficit
            // is a physical fact that must produce a crisis), not a content bug's.
            "res" -> {
                val nn = { x: Double -> max(0.0, app(x)) }
                when (k) {
                    "food" -> s.food = nn(s.food); "water" -> s.water = nn(s.water)
                    "spares" -> s.spares = nn(s.spares); "medicine" -> s.medicine = nn(s.medicine)
                    "deltaV" -> s.deltaV = nn(s.deltaV); "cultivated" -> s.cultivated = nn(s.cultivated)
                    "data" -> s.data = nn(s.data)
                    // NOT clamped at zero: `res.energy` is a signed power COMMITMENT, and
                    // committing power is what most of the corpus uses it for (D008). Clamping it
                    // made every `res.energy -= N` a no-op from a standing start.
                    "energy" -> s.powerCommit = app(s.powerCommit)
                }
            }
            "pop" -> when (k) {
                "adults" -> s.adults = maxOf(0.0, app(s.adults))
                "children" -> s.children = maxOf(0.0, app(s.children))
                "elders" -> s.elders = maxOf(0.0, app(s.elders))
                "cryo" -> s.cryo = maxOf(0.0, app(s.cryo))
            }
            // Gauges and pressures are hard-bounded (D003 P2: no unbounded gauges, ever).
            //
            // A gauge effect written by CONTENT moves two things: the gauge now, and the level the
            // gauge returns to. Without the second the first is erased inside a year by
            // `Physics.gauges`, whose target reads only the hidden pressures — so every "gains: a
            // good deal of standing" in the corpus was a promise the simulation quietly broke, and
            // two playtests caught it by watching their own status line.
            //
            // Physics writes gauges too (starvation, brownouts); it calls `Physics.setGauge`
            // instead, which does not touch the shift. Only a decision changes where a colony
            // settles — which is the game's core promise stated mechanically.
            "gauge" -> s.gauge[k]?.let {
                s.gauge[k] = app(it).coerceIn(0.0, 100.0)
                val delta = when (op) { "+=" -> v; "-=" -> -v; else -> 0.0 }
                s.gaugeShift[k]?.let { cur -> s.gaugeShift[k] = (cur + delta).coerceIn(-25.0, 25.0) }
            }
            "pressure" -> s.pressure[k]?.let { s.pressure[k] = app(it).coerceIn(0.0, 1.0) }
        }
    }

    private val EFF = Regex("""^\s*([A-Za-z_][A-Za-z0-9_.]*)\s*(\+=|-=|=)\s*(-?[0-9.]+)\s*$""")
    private val SCHED = Regex("""^schedule:([A-Za-z0-9_]+)\s+in\s+([0-9]+)(?:\.\.([0-9]+))?y$""")
    private val FLAGSET = Regex("""^flag:([A-Za-z0-9_]+)\s*=\s*true(?:\s+for\s+([0-9]+)y)?$""")
    private val FLAGCLEAR = Regex("""^flag:([A-Za-z0-9_]+)\s*=\s*false$""")
    private val DAMAGE = Regex("""^damage:(\S+)\s+(-?[0-9.]+)$""")   // negative = repair
    private val KILL = Regex("""^kill:([0-9.]+)$""")

    fun apply(s: WorldState, raw: String, rng: Random, bindings: Map<String, String>, thread: Thread? = null) {
        var e = raw.trim()
        for ((k, v) in bindings) e = e.replace("{$k}", v)

        SCHED.find(e)?.let { m ->
            val (id, lo, hi) = m.destructured
            val a = lo.toInt(); val b = if (hi.isEmpty()) a else hi.toInt()
            val delay = if (b > a) rng.nextInt(a, b + 1) else a
            val t = Thread(familyId = id, stage = (thread?.stage ?: 0) + 1,
                           dueMonth = s.month + delay * 12)
            t.bindings.putAll(bindings)
            thread?.vars?.let { t.vars.putAll(it) }     // an arc remembers its own history
            s.threads.add(t)
            return
        }
        FLAGCLEAR.find(e)?.let { m ->
            s.flags.remove(m.destructured.component1())
            return
        }
        FLAGSET.find(e)?.let { m ->
            val (name, dur) = m.destructured
            s.flags[name] = FlagVal(s.month, if (dur.isEmpty()) -1 else s.month + dur.toInt() * 12)
            return
        }
        // The colony can GAIN, not only lose. Without these two the capability set is monotonically
        // decreasing, and every `needs X` lock in the corpus is a permanent one — which is what two
        // playtests measured: 65 and 98 years, nineteen distinct gates, none ever opened.
        if (e.startsWith("install:")) {
            val id = e.removePrefix("install:").trim()
            if (Content.module(id) != null && !s.installed.contains(id)) {
                s.installed.add(id)
                s.moduleHealth[id] = 1.0
                s.changes.add("built: " + (Content.module(id)?.name ?: id))
            }
            return
        }
        if (e.startsWith("grant:")) {
            val c = e.removePrefix("grant:").trim()
            if (s.granted.add(c)) s.changes.add("the colony can now " + Content.capVerb(c))
            return
        }
        if (e.startsWith("revoke:")) {
            val c = e.removePrefix("revoke:").trim()
            if (s.granted.remove(c)) s.changes.add("the colony can no longer " + Content.capVerb(c))
            return
        }
        DAMAGE.find(e)?.let { m ->
            val (mid, amt) = m.destructured
            if (s.moduleHealth.containsKey(mid))
                s.moduleHealth[mid] = ((s.moduleHealth[mid] ?: 1.0) - amt.toDouble()).coerceIn(0.0, 1.0)
            return
        }
        KILL.find(e)?.let { m ->
            Physics.kill(s, m.destructured.component1().toDouble(), rng)
            return
        }
        if (e.startsWith("ambition:")) {
            val id = e.removePrefix("ambition:").trim()
            if (AMBITIONS.any { it.id == id } && s.ambitions.none { it.id == id })
                s.ambitions.add(Ambition(id))
            return
        }
        if (e.startsWith("phase:")) {
            s.phase = Phase.valueOf(e.removePrefix("phase:").trim())
            if (s.phase == Phase.SURFACE && s.landfallMonth < 0) s.landfallMonth = s.month
            return
        }
        if (e.startsWith("thread.var:")) {
            val m = EFF.find("x." + e.removePrefix("thread.var:"))
            if (thread != null && m != null) {
                val (p, op, v) = m.destructured
                val key = p.removePrefix("x.")
                val cur = thread.vars[key] ?: 0.0
                thread.vars[key] = when (op) { "+=" -> cur + v.toDouble(); "-=" -> cur - v.toDouble(); else -> v.toDouble() }
            }
            return
        }
        val m = EFF.find(e) ?: error("unparseable effect: '$raw'")
        val (path, op, v) = m.destructured
        bump(s, path, op, v.toDouble())
    }
}
